// ** MUI Imports
// import Fab from '@mui/material/Fab'
import AppBar from '@mui/material/AppBar'
import { styled } from '@mui/material/styles'
import Box, { BoxProps } from '@mui/material/Box'
import MuiToolbar, { ToolbarProps } from '@mui/material/Toolbar'
import { Card, List, useMediaQuery } from '@mui/material'

// ** Icon Imports
// import Icon from 'src/@core/components/icon'

// ** Theme Config Import
import themeConfig from 'src/configs/themeConfig'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

// ** Components
// import Footer from './components/shared-components/footer'
import Navigation from './components/horizontal/navigation'

// import ScrollToTop from 'src/@core/components/scroll-to-top'
import AppBarContent from './components/horizontal/app-bar-content'
import ChildPageNavigation from './components/horizontal/navigation/childPage'

import { HorizontalNavItemsType } from '@/src/@core/layouts/types'
import { useGlobalState } from '@/src/pages/_app'
import {
  FranchiseSubNavigation,
  ProductSubNavigation,
  SubNavigation,
  SubNavigationMobile,
  SubNavigationRestaurant
} from '@/src/gyg-navigation/vertical'
import { useAuth } from '@/src/hooks/useAuth'

const HorizontalLayoutWrapper = styled('div')({
  height: '100%',
  display: 'flex',
  ...(themeConfig.horizontalMenuAnimation && { overflow: 'clip' })
})

const MainContentWrapper = styled(Box)<BoxProps>({
  flexGrow: 1,
  minWidth: 0,
  display: 'flex',

  // minHeight: '100vh',
  flexDirection: 'column'
})

const Toolbar = styled(MuiToolbar)<ToolbarProps>(({ theme }) => ({
  width: '100%',
  padding: `${theme.spacing(0, 6)} !important`,
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(4)
  },
  [theme.breakpoints.down('xs')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}))

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.spacing(6),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  }
}))

const HorizontalLayout = (props: LayoutProps) => {
  const auth = useAuth()

  // ** Props
  const {
    hidden,
    children,
    settings,

    // scrollToTop,
    // footerProps,
    saveSettings,
    contentHeightFixed,
    horizontalLayoutProps
  } = props

  const { isChildPage, isProductPage, isFranchisePage } = useGlobalState()

  // Call navigation functions at component level to avoid hooks order issues
  const subNavigationItems = SubNavigation()
  const subNavigationMobileItems = SubNavigationMobile()
  const subNavigationRestaurantItems = SubNavigationRestaurant()
  const productSubNavigationItems = ProductSubNavigation()
  const franchiseSubNavigationItems = FranchiseSubNavigation()

  // ** Vars
  const { skin, appBar, navHidden, appBarBlur, contentWidth, isTabletMenuOpen, isMobileMenuOpen } = settings
  const appBarProps = horizontalLayoutProps?.appBar?.componentProps
  const userNavMenuContent = horizontalLayoutProps?.navMenu?.content

  let userAppBarStyle = {}
  if (appBarProps && appBarProps.sx) {
    userAppBarStyle = appBarProps.sx
  }
  const userAppBarProps = Object.assign({}, appBarProps)
  delete userAppBarProps.sx

  const isMobile = useMediaQuery('(max-width:540px) and (orientation: portrait)')
  const isTablet = useMediaQuery('(max-width:1100px) and (orientation: landscape)')
  const isTabletPortrait = useMediaQuery('(max-width:1150px) and (orientation: portrait)')

  return (
    <HorizontalLayoutWrapper className='layout-wrapper' style={{ flexDirection: 'column' }}>
      {/* Navbar (or AppBar) and Navigation Menu Wrapper */}
      {(isTablet || isTabletPortrait) && !isMobile && (
        <AppBar
          color='default'
          elevation={skin === 'bordered' ? 0 : 2}
          className='layout-navbar-and-nav-container'
          position={appBar === 'fixed' ? 'sticky' : 'static'}
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            justifyContent: 'center',
            ...(appBar === 'static' && { zIndex: 13 }),
            transition: 'border-bottom 0.2s ease-in-out',
            ...(appBarBlur && { backdropFilter: 'blur(6px)' }),
            backgroundColor: 'text.primary',
            ...(skin === 'bordered' && { borderBottom: theme => `1px solid ${theme.palette.divider}` }),
            ...userAppBarStyle
          }}
          {...userAppBarProps}
        >
          {/* Navbar / AppBar */}
          <Box
            className='layout-navbar'
            sx={{
              width: '100%',
              background: 'none',
              ...(navHidden ? {} : { borderBottom: theme => `1px solid ${theme.palette.divider}` })
            }}
          >
            <Toolbar
              className='navbar-content-container'
              sx={{
                mx: 'auto',
                ...(contentWidth === 'boxed' && { '@media (min-width:1440px)': { maxWidth: 1440 } }),
                minHeight: theme => `${theme.mixins.toolbar.minHeight as number}px !important`
              }}
            >
              <AppBarContent
                {...props}
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                appBarContent={horizontalLayoutProps?.appBar?.content}
                appBarBranding={horizontalLayoutProps?.appBar?.branding}
              />
            </Toolbar>
          </Box>
          {/* Navigation Menu */}
          <Box
            className='layout-horizontal-nav'
            sx={{
              width: '100%',
              ...horizontalLayoutProps?.navMenu?.sx,
              padding: isMobile ? '0 8px 16px 8px' : '0 20px 16px 20px',
              overflowX: 'hidden',
              overflowY: isTabletMenuOpen || isMobileMenuOpen ? 'auto' : 'hidden',
              maxHeight: isTabletMenuOpen || isMobileMenuOpen ? '400px' : '0px',
              opacity: isTabletMenuOpen || isMobileMenuOpen ? 1 : 0,
              transform:
                isTabletMenuOpen || isMobileMenuOpen
                  ? 'translateY(0)'
                  : isMobile
                    ? 'translateY(100%)'
                    : 'translateY(-100%)',
              transition:
                'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out, transform 0.3s ease-in-out',
              ...(isTabletMenuOpen || isMobileMenuOpen ? {} : { padding: '0 20px 0 20px' })
            }}
          >
            <Toolbar
              className='horizontal-nav-content-container'
              sx={{
                px: '0 !important',
                mx: 'auto',
                ...(contentWidth === 'boxed' && { '@media (min-width:1440px)': { maxWidth: 1440 } }),
                minHeight: theme =>
                  `${(theme.mixins.toolbar.minHeight as number) - 4 - (skin === 'bordered' ? 1 : 0)}px !important`
              }}
            >
              {(userNavMenuContent && userNavMenuContent(props)) || (
                <Navigation
                  {...props}
                  horizontalNavItems={
                    (horizontalLayoutProps as NonNullable<LayoutProps['horizontalLayoutProps']>)?.navMenu?.navItems
                  }
                />
              )}
            </Toolbar>
          </Box>
        </AppBar>
      )}
      <MainContentWrapper
        className='layout-content-wrapper'
        sx={{
          ...(contentHeightFixed && { maxHeight: '100vh' }),
          backgroundColor: 'text.primary',
          ...(isTabletMenuOpen &&
            isMobile && {
              paddingTop: '300px',
              paddingBottom: '0px !important',
              border: 'none',
              transform: 'translateY(-300px)',
              transition:
                'max-height 0.5s ease-in-out, opacity 0.5s ease-in-out, padding 0.5s ease-in-out, transform 0.5s ease-in-out'
            })
        }}
      >
        {/* Content */}
        <Card
          sx={{
            background: '#f8f8f8',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'text.primary',
            ...(isMobile
              ? {
                  borderTopLeftRadius: '0',
                  borderTopRightRadius: '0',
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px',
                  paddingBottom: '64px'
                }
              : {
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                  borderBottomLeftRadius: '0',
                  borderBottomRightRadius: '0'
                })
          }}
        >
          {/**
           * Sub Navigation for Child Pages
           */}
          {(isChildPage || isProductPage || isFranchisePage) && (
            <List component='div' sx={{ backgroundColor: 'secondary.main', px: 3 }}>
              {isChildPage && (
                <Box className='horizontal-sub-nav test'>
                  <ChildPageNavigation
                    {...props}
                    horizontalNavItems={
                      auth.user?.role.name === 'Restaurant Team'
                        ? (subNavigationRestaurantItems as HorizontalNavItemsType)
                        : isMobile
                          ? (subNavigationMobileItems as HorizontalNavItemsType)
                          : (subNavigationItems as HorizontalNavItemsType)
                    }
                  />
                </Box>
              )}
              {isProductPage && (
                <Box className='horizontal-sub-nav'>
                  <ChildPageNavigation
                    {...props}
                    horizontalNavItems={productSubNavigationItems as HorizontalNavItemsType}
                  />
                </Box>
              )}
              {isFranchisePage && (
                <Box className='horizontal-sub-nav'>
                  <ChildPageNavigation
                    {...props}
                    horizontalNavItems={franchiseSubNavigationItems as HorizontalNavItemsType}
                  />
                </Box>
              )}
            </List>
          )}
          <ContentWrapper
            className='layout-page-content'
            sx={{
              ...(contentHeightFixed && { display: 'flex', overflow: 'hidden' }),
              ...(contentWidth === 'boxed' && {
                mx: 'auto',
                '@media (min-width:1440px)': { maxWidth: 1440 },
                '@media (min-width:1200px)': { maxWidth: '100%' }
              }),
              '@media (max-width:900px)': {
                padding: '0 4px 4px 4px'
              }
            }}
          >
            {children}
          </ContentWrapper>
        </Card>

        {/* Footer */}
        {/* <Footer {...props} footerStyles={footerProps?.sx} footerContent={footerProps?.content} /> */}
        {/* Scroll to top button */}
        {/* {scrollToTop ? (
          scrollToTop(props)
        ) : (
          <ScrollToTop className='mui-fixed'>
            <Fab color='primary' size='small' aria-label='scroll back to top'>
              <Icon icon='tabler:arrow-up' />
            </Fab>
          </ScrollToTop>
        )} */}
      </MainContentWrapper>
      {isMobile && (
        <AppBar
          color='default'
          elevation={skin === 'bordered' ? 0 : 2}
          className='layout-navbar-and-nav-container'
          position={appBar === 'fixed' ? 'sticky' : 'static'}
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            justifyContent: 'center',
            ...(appBar === 'static' && { zIndex: 13 }),
            transition: 'border-bottom 0.2s ease-in-out',
            ...(appBarBlur && { backdropFilter: 'blur(6px)' }),
            backgroundColor: 'text.primary',
            ...(skin === 'bordered' && { borderBottom: theme => `1px solid ${theme.palette.divider}` }),
            ...userAppBarStyle,
            position: 'fixed',
            bottom: 0,
            top: 'auto'
          }}
          {...userAppBarProps}
        >
          {/* Navbar / AppBar */}
          <Box
            className='layout-navbar'
            sx={{
              width: '100%',
              background: 'none',
              ...(navHidden ? {} : { borderBottom: theme => `1px solid ${theme.palette.divider}` })
            }}
          >
            <Toolbar
              className='navbar-content-container'
              sx={{
                mx: 'auto',
                ...(contentWidth === 'boxed' && { '@media (min-width:1440px)': { maxWidth: 1440 } }),
                minHeight: theme => `${theme.mixins.toolbar.minHeight as number}px !important`
              }}
            >
              <AppBarContent
                {...props}
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                appBarContent={horizontalLayoutProps?.appBar?.content}
                appBarBranding={horizontalLayoutProps?.appBar?.branding}
              />
            </Toolbar>
          </Box>
          {/* Navigation Menu */}
          <Box
            className='layout-horizontal-nav'
            sx={{
              width: '100%',
              ...horizontalLayoutProps?.navMenu?.sx,
              padding: isMobile ? '0 8px 16px 8px' : '0 20px 16px 20px',
              overflowX: 'hidden',
              overflowY: isTabletMenuOpen || isMobileMenuOpen ? 'auto' : 'hidden',
              maxHeight: isTabletMenuOpen || isMobileMenuOpen ? '400px' : '0px',
              opacity: isTabletMenuOpen || isMobileMenuOpen ? 1 : 0,
              transform: 'translateY(0)',
              transition:
                'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out, transform 0.3s ease-in-out',
              ...(isTabletMenuOpen || isMobileMenuOpen ? {} : { padding: '0 20px 0 20px' })
            }}
          >
            <Toolbar
              className='horizontal-nav-content-container'
              sx={{
                px: '0 !important',
                mx: 'auto',
                ...(contentWidth === 'boxed' && { '@media (min-width:1440px)': { maxWidth: 1440 } }),
                minHeight: theme =>
                  `${(theme.mixins.toolbar.minHeight as number) - 4 - (skin === 'bordered' ? 1 : 0)}px !important`
              }}
            >
              {(userNavMenuContent && userNavMenuContent(props)) || (
                <Navigation
                  {...props}
                  horizontalNavItems={
                    (horizontalLayoutProps as NonNullable<LayoutProps['horizontalLayoutProps']>)?.navMenu?.navItems
                  }
                />
              )}
            </Toolbar>
          </Box>
        </AppBar>
      )}
    </HorizontalLayoutWrapper>
  )
}

export default HorizontalLayout
